import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import Immutable from 'immutable';

import { setModalPromoCode } from 'actions/modals';
import * as modalActions from 'actions/modals';

import { getCurrentUser, getRolloutEnabled } from 'selectors/session';

import { changeToNewTier } from 'features/biller/slice';
import {
  openCalendlyEnterpriseModalCbAction,
  openCalendlyModalCbAction,
  openCheckoutModalCbAction,
  openSawtoothSurvey,
  showClickToCallDialogCbAction,
} from 'features/commandBar/commandBarCallbackActions';

import { CalendlyEnterpriseModal } from 'components/CalendlyEnterpriseModal/CalendlyEnterpriseModal';
import { useCalendlyEnterpriseModalHandlers } from 'components/CalendlyEnterpriseModal/hooks';
import { CalendlyModal } from 'components/CalendlyModal/CalendlyModal';
import { useCalendlyModalHandlers } from 'components/CalendlyModal/hooks';

import { useRegisterCommandBarCallbackAction } from 'util/commandBar';
import { EVENT_CATEGORIES, PRODUCT_AREAS } from 'util/tracking_constants';
import { withUxRoot } from 'util/uxEvents';

export type CommandBarProps = {
  children: React.ReactNode;
  currentUser: Immutable.Map<string, string>;
  onChangeToNewTier: ({
    tierName,
    workflowSource,
    billingFrequency,
  }: {
    tierName: string;
    workflowSource: string;
    billingFrequency: string;
  }) => void;
  onSetModalPromoCode: (promoCode: string) => void;
  showClickToCallDialog: () => void;
  monetizationCommandBarEnabled: boolean;
};

export const CommandBar = ({
  children,
  currentUser,
  onChangeToNewTier,
  onSetModalPromoCode,
  showClickToCallDialog,
  monetizationCommandBarEnabled,
}: CommandBarProps) => {
  const { calendlyModalIsOpen, handleCalendlyClick, handleCalendlyClose } =
    useCalendlyModalHandlers();
  const {
    calendlyEnterpriseModalIsOpen,
    handleCalendlyEnterpriseClick,
    handleCalendlyEnterpriseClose,
  } = useCalendlyEnterpriseModalHandlers();
  const [calendlyUrl, setCalendlyUrl] = useState<string | undefined>('');
  const [calendlyEnterpriseUrl, setCalendlyEnterpriseUrl] = useState<
    string | undefined
  >('');
  const isEmbedded = useIsEmbedded();

  const openCalendlyModal = useCallback(
    ({ url }: { url?: string }) => {
      setCalendlyUrl(url);
      handleCalendlyClick();
    },
    [handleCalendlyClick]
  );

  const openCalendlyEnterpriseModal = useCallback(
    ({ url }: { url?: string }) => {
      setCalendlyEnterpriseUrl(url);
      handleCalendlyEnterpriseClick();
    },
    [handleCalendlyEnterpriseClick]
  );

  useRegisterCommandBarCallbackAction(
    'openCheckoutModal',
    openCheckoutModalCbAction({
      onChangeToNewTier,
      setModalPromoCode: onSetModalPromoCode,
    })
  );
  useRegisterCommandBarCallbackAction(
    'openCalendlyModal',
    openCalendlyModalCbAction({
      openCalendlyModal,
    })
  );
  useRegisterCommandBarCallbackAction(
    'openCalendlyEnterpriseModal',
    openCalendlyEnterpriseModalCbAction({
      openCalendlyEnterpriseModal,
    })
  );
  useRegisterCommandBarCallbackAction(
    'openSawtoothSurvey',
    openSawtoothSurvey({ userId: currentUser?.get('id') })
  );
  useRegisterCommandBarCallbackAction(
    'showClickToCallDialog',
    showClickToCallDialogCbAction({
      showClickToCallDialog,
    })
  );

  if (
    isEmbedded ||
    !monetizationCommandBarEnabled ||
    window.location.pathname.includes('/admin')
  ) {
    return children || null;
  }

  return (
    <>
      {children || null}
      {calendlyModalIsOpen && calendlyUrl && (
        <CalendlyModal
          url={calendlyUrl}
          open={calendlyModalIsOpen}
          handleCloseModal={handleCalendlyClose}
        />
      )}
      {calendlyEnterpriseModalIsOpen && calendlyEnterpriseUrl && (
        <CalendlyEnterpriseModal
          origin="command_ai"
          currentUser={currentUser}
          url={calendlyEnterpriseUrl}
          open={calendlyEnterpriseModalIsOpen}
          handleCloseModal={handleCalendlyEnterpriseClose}
        />
      )}
    </>
  );
};

export default connect(
  state => ({
    currentUser: getCurrentUser(state),
    monetizationCommandBarEnabled: getRolloutEnabled(
      state,
      'monetization_command_bar'
    ),
  }),
  {
    onChangeToNewTier: changeToNewTier,
    onSetModalPromoCode: setModalPromoCode,
    showClickToCallDialog: modalActions.showClickToCallDialog,
  }
)(
  withUxRoot({
    productArea: PRODUCT_AREAS.COMMAND_BAR,
    eventCategory: EVENT_CATEGORIES.COMMAND_BAR,
  })(CommandBar)
);
